"use client";

import { useRef, useState, type FC, type Ref } from "react";
import { debounce } from "@huub-web/utils";

import type {
  FormInputType,
  DataFieldValue,
  FieldConstraints,
} from "../../types";
import {
  FormInputField,
  type FormInputFieldProps,
} from "./components/FormInputField";
import { useDynamicFormInput } from "../../hooks/useDynamicFormInput";
import { useOnce } from "../../hooks/useOnce";
import { useUuid } from "../../hooks/useUuid";
import { FormRow, type FormRowProps } from "../FormRow";

import { defaultInputStyle } from "./FormInput.css";
import { mergeRefs } from "../../utils/mergeRefs";

export type FormInputProps = {
  id?: string;
  type?: FormInputType;
  label?: string;
  description?: string;
  help?: string;
  value?: DataFieldValue;
  selectOptions?: {
    label: string;
    value: DataFieldValue;
  }[];
  selectSearch?: boolean;
  required?: boolean;
  error?: string;
  basis?: string;
  info?: string;
  placeholder?: string;
  /**
   * Dynamic validation constraints from backend json_schema_extra.
   */
  constraints?: FieldConstraints;
  success?: boolean;
  disabled?: boolean;
  highlight?: boolean;
  variant?: FormRowProps["variant"];
  onChange?: FormInputFieldProps["onChange"];
  onValidation?: FormInputFieldProps["onValidation"];
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * FormInput component is a form input field with a label and a value display.
 */
export const FormInput: FC<FormInputProps> = ({
  id,
  type = "string",
  disabled = false,
  success = false,
  highlight = false,
  required = false,
  selectSearch = false,
  basis,
  error,
  label,
  description,
  help,
  variant,
  value,
  selectOptions,
  placeholder,
  constraints,
  onChange,
  onValidation,
  ref,
}) => {
  const fieldRef = useRef<HTMLDivElement>(null);
  const uuid = useUuid();
  const [constraintError, setConstraintError] = useState<string | undefined>();

  const { parsedType, isInvalidType } = useDynamicFormInput({
    type,
    value,
  });

  useOnce(() => {
    if (!id) {
      console.warn(`ID is required for FormInput ${type}`);
    }
  }, [id]);

  /**
   * Tracks dynamic validation results to surface constraint errors
   * in the FormRow error display for non-toggle variants.
   * Debounced to match FormInputField's 500ms validation debounce,
   * preventing error text from flickering while the user is still typing.
   */
  const setConstraintErrorDebounced = useRef(debounce(setConstraintError, 500));
  const handleValidation: FormInputFieldProps["onValidation"] = (result) => {
    setConstraintErrorDebounced.current(
      result.success === false ? result.errors?.[0] : undefined,
    );
    onValidation?.(result);
  };

  const labelInFormRow = ["checkboxes", "radio", "boolean"].includes(type);

  return type === "hidden" ? (
    <FormInputField
      ref={mergeRefs(ref, fieldRef)}
      id={uuid}
      label={labelInFormRow ? undefined : label}
      fieldKey={id}
      highlight={highlight}
      className={defaultInputStyle}
      variant="default"
      type={parsedType}
      isInvalidType={isInvalidType}
      value={value}
      onChange={onChange}
      onValidation={handleValidation}
      constraints={constraints}
      selectOptions={selectOptions}
      selectSearch={selectSearch}
      active={false}
      autoFocus={false}
      error={error}
      placeholder={placeholder}
      success={success}
      disabled={disabled}
      required={required}
      info={help}
    />
  ) : (
    <FormRow
      ref={ref}
      label={labelInFormRow ? label : undefined}
      description={description}
      required={required}
      error={error ?? constraintError}
      variant={
        type === "boolean" || type === "checkboxes" ? "autoSpace" : variant
      }
      style={{ flexBasis: basis, minWidth: basis ? 0 : undefined }}
      grow={!basis}
    >
      <FormInputField
        ref={fieldRef}
        id={uuid}
        label={labelInFormRow ? undefined : label}
        fieldKey={id}
        highlight={highlight}
        className={defaultInputStyle}
        variant="default"
        type={parsedType}
        isInvalidType={isInvalidType}
        value={value}
        onChange={onChange}
        onValidation={handleValidation}
        constraints={constraints}
        selectOptions={selectOptions}
        selectSearch={selectSearch}
        active={false}
        autoFocus={false}
        error={error}
        placeholder={placeholder}
        success={success}
        disabled={disabled}
        required={required}
        info={help}
      />
    </FormRow>
  );
};
